import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { IconEye } from "@/assets/icons/geist/IconEye";
import { IconEyeOff } from "@/assets/icons/geist/IconEyeOff";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";
import { AgentModeDropdown } from "@/components/ai/prompt-input/AgentModeDropdown";
import { AgentMode, applyAgentModePrefix, cycleAgentMode } from "@/components/ai/prompt-input/agent-mode";
import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { SupportBundleUnavailableModal } from "@/components/ai/SupportBundleUnavailableModal";
import { Button } from "@/components/flexkit/Button";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import type { CommandSuggestion } from "@/components/podkit/forms/TextareaWithSuggestions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { useFlag, useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { Flags } from "@/feature-flags";
import { type FollowModeState } from "@/hooks/use-follow-mode";
import { isTouchDevice } from "@/utils/is-touch-device";
import { useMembership } from "@/hooks/use-membership";
import { usePersistedInput } from "@/hooks/use-persisted-input";
import { useSendToAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { formatError } from "@/utils/errors";
import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import type React from "react";
import { useCallback, useMemo, useState, type FC } from "react";
import { useAtom } from "jotai";
import { v4 as uuidv4 } from "uuid";
import ChatInputWithPrompts from "@/components/ai/chat/ChatInputWithPrompts";

export type NewPendingMessage = {
    id: string;
    // If content is undefined, it means the message with `id` failed to be sent
    // and should be retracted again.
    content?: string;
};

export type ChatInputProps = {
    agentExecution: PlainAgentExecution;
    environmentId?: string;
    placeholder?: string;
    onNewMessage: (message: NewPendingMessage) => void;
    hasMessages: boolean;
    followMode?: FollowModeState;
    autoFocus?: React.TextareaHTMLAttributes<HTMLTextAreaElement>["autoFocus"];
} & PropsWithClassName;

export const ChatInput: FC<ChatInputProps> = ({
    agentExecution,
    environmentId,
    onNewMessage,
    className,
    placeholder,
    followMode,
    autoFocus,
}) => {
    const { value: isOnaAgentCommandsEnabled } = useFlagOnaAIEnabled();
    const { value: isRalphModeEnabled } = useFlag(Flags.RalphModeEnabled, false);
    const { value: isSpecModeEnabled } = useFlag(Flags.SpecModeEnabled, false);
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    const sendToAgentExecution = useSendToAgentExecution();
    const { handleStopAgentExecution, downloadSupportBundle, isSupportBundleAvailable } =
        useAgentExecutionActions(agentExecution);
    const { toast } = useToast();

    const storageKey = `agent-execution-input-${agentExecution.id}`;
    const [inputValue, setInputValue] = usePersistedInput(storageKey, "");
    const [showDeleteAgentExecutionModal, setShowDeleteAgentExecutionModal] = useState(false);
    const [showSupportBundleUnavailableModal, setShowSupportBundleUnavailableModal] = useState(false);
    const [agentMode, setAgentMode] = useAtom(agentModeAtomFamily(environmentId ?? ""));

    // Build available agent modes based on feature flags
    const availableAgentModes = useMemo(() => {
        const modes: AgentMode[] = [AgentMode.Agent];
        if (isSpecModeEnabled) {
            modes.push(AgentMode.Spec);
        }
        if (isRalphModeEnabled) {
            modes.push(AgentMode.Ralph);
        }
        return modes;
    }, [isSpecModeEnabled, isRalphModeEnabled]);

    const showAgentModeDropdown = availableAgentModes.length > 1;

    const onSubmit = useCallback(
        async (text: string, messageId: string) => {
            if (!agentExecution.id) {
                return;
            }
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecution.id,
                    userInput: new UserInputBlock({
                        id: messageId,
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: text,
                            }),
                        },
                    }),
                }),
            );
        },
        [agentExecution.id, sendToAgentExecution],
    );

    const handleSendMessage = useCallback(
        async (value: string) => {
            const trimmedValue = value.trim();
            if (!trimmedValue) {
                return;
            }

            // Handle built-in commands (only if feature flag is enabled)
            if (isOnaAgentCommandsEnabled) {
                if (trimmedValue === "/clear") {
                    setInputValue("");
                    setShowDeleteAgentExecutionModal(true);
                    return;
                }

                if (trimmedValue === "/commands" && isOrgAdmin) {
                    setInputValue("");
                    window.open("/settings/agents", "_blank");
                    return;
                }
                if (trimmedValue === "/support-bundle") {
                    setInputValue("");
                    if (isSupportBundleAvailable) {
                        void downloadSupportBundle();
                    } else {
                        setShowSupportBundleUnavailableModal(true);
                    }
                    return;
                }
            }

            if (sendToAgentExecution.isPending) {
                return;
            }

            const messageToSend = applyAgentModePrefix(trimmedValue, agentMode);

            const id = uuidv4();
            try {
                setInputValue("");
                onNewMessage({ id, content: trimmedValue });
                await onSubmit(messageToSend, id);
            } catch (error) {
                setInputValue(value);
                onNewMessage({ id, content: undefined });
                toast({
                    title: "Error sending to conversation",
                    description: formatError(error),
                });
            }
        },
        [
            sendToAgentExecution.isPending,
            setInputValue,
            onNewMessage,
            onSubmit,
            toast,
            isOnaAgentCommandsEnabled,
            isOrgAdmin,
            downloadSupportBundle,
            isSupportBundleAvailable,
            setShowSupportBundleUnavailableModal,
            agentMode,
        ],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                await handleStopAgentExecution();
                return;
            }
            // Shift+Tab cycles agent mode
            if (e.shiftKey && e.key === "Tab" && showAgentModeDropdown) {
                e.preventDefault();
                setAgentMode(cycleAgentMode(agentMode, availableAgentModes));
                return;
            }
            // On touch devices, let Enter insert newlines (users submit via Send button)
            // On desktop, Enter submits and Shift+Enter inserts newlines
            if (e.key === "Enter" && !e.shiftKey && !isTouchDevice()) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                await handleSendMessage(input.value);
                return;
            }
        },
        [handleStopAgentExecution, handleSendMessage, showAgentModeDropdown, agentMode, setAgentMode, availableAgentModes],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const handleSuggestionSelect = useCallback(
        (suggestion: CommandSuggestion) => {
            // Check if this is a builtin command
            if (suggestion.id.startsWith("builtin-")) {
                // Execute builtin commands directly
                if (suggestion.value === "/clear") {
                    setShowDeleteAgentExecutionModal(true);
                    return true; // Prevent default pasting behavior
                }

                if (suggestion.value === "/commands" && isOrgAdmin) {
                    window.open("/settings/agents", "_blank");
                    return true; // Prevent default pasting behavior
                }

                if (suggestion.value === "/support-bundle") {
                    if (isSupportBundleAvailable) {
                        void downloadSupportBundle();
                    } else {
                        setShowSupportBundleUnavailableModal(true);
                    }
                    return true; // Prevent default pasting behavior
                }
            }
            // For non-builtin commands, allow the default pasting behavior
            return false;
        },
        [isOrgAdmin, downloadSupportBundle, isSupportBundleAvailable, setShowSupportBundleUnavailableModal],
    );

    const isInputEmpty = !inputValue.trim();

    return (
        <>
            <div
                className={cn(
                    "relative flex w-full flex-col gap-1 rounded-lg border border-border-base bg-surface-pure pb-2 pl-4 pr-0 pt-4 shadow-sm",
                )}
            >
                <ChatInputWithPrompts
                    agentExecution={agentExecution}
                    minHeight={40}
                    maxHeight={300}
                    popoverSide="top"
                    popoverSideOffset={20} // top padding + border + some spacing
                    popoverWidthOffset={16} // account for the total padding (only left padding at the moment)
                    popoverAlignOffset={-16} // account for the left padding
                    placeholder={placeholder}
                    onKeyDown={onKeyDown}
                    onChange={handleChange}
                    onSuggestionSelect={handleSuggestionSelect}
                    value={inputValue}
                    className={className}
                    data-testid="agent-execution-chat-input"
                    autoFocus={autoFocus}
                    enterKeyHint="enter"
                    disabledMessage={
                        agentMode === AgentMode.Ralph
                            ? "Commands are not available in Ralph mode. Switch to Agent mode to use commands."
                            : undefined
                    }
                />
                <div className="flex items-center gap-2 self-end pr-2">
                    {followMode && (
                        <Tooltip content={followMode.enabled ? "Stop following" : "Follow"}>
                            <Button
                                variant="ghost"
                                className="hidden size-9 p-0 md:flex"
                                LeadingIcon={followMode.enabled ? IconEyeOff : IconEye}
                                aria-label={followMode.enabled ? "Stop following" : "Follow"}
                                onClick={() => followMode.toggle()}
                                data-tracking-id="toggle-follow-mode"
                            />
                        </Tooltip>
                    )}

                    {showAgentModeDropdown && (
                        <AgentModeDropdown
                            mode={agentMode}
                            onModeChange={setAgentMode}
                            availableModes={availableAgentModes}
                        />
                    )}

                    {/* Send Button */}
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                Send
                                <div className="flex items-center gap-1">
                                    <KeyboardKey>Enter</KeyboardKey>
                                </div>
                            </div>
                        }
                    >
                        <Button
                            id="send-button"
                            className="size-8 p-0"
                            LeadingIcon={IconArrowUp}
                            aria-label="Send message"
                            onClick={() => handleSendMessage(inputValue)}
                            disabled={isInputEmpty || sendToAgentExecution.isPending}
                            data-tracking-id="send-to-agent-execution-chat-input"
                        />
                    </Tooltip>
                </div>
            </div>

            <DeleteAgentExecutionModal
                agentExecution={agentExecution}
                open={showDeleteAgentExecutionModal}
                onClose={() => setShowDeleteAgentExecutionModal(false)}
                inputValue={inputValue}
            />

            <SupportBundleUnavailableModal
                open={showSupportBundleUnavailableModal}
                onClose={() => setShowSupportBundleUnavailableModal(false)}
                agentExecution={agentExecution}
            />
        </>
    );
};
